import {
  audioManager,
  corePhasesManager,
  game,
  gauss,
  modes,
  PlayerSex,
  playersManager,
  PlayersSortTypes,
  THREE,
  gsap
} from '@powerplay/core-minigames'
import type { Athlete } from './Athlete'
import { FlightPath } from '../env/FlightPath'
import { disciplinePhasesManager } from '@/app/phases/DisciplinePhasesManager'
import { javelinConfig } from '@/app/config'
import {
  gamePhaseState,
  mainState,
  miniTableState,
  textMessageMetersState
} from '@/stores'
import {
  stateManager,
  StateManager
} from '@/app/StateManager'
import { AudioNames } from '@/app/types'
import { endManager } from '@/app/EndManager'
import { audioHelper } from '@/app/audioHelper/AudioHelper'
import { player } from './player'

export class Javelin {

  /** Objekt ostepu */
  public javelinObject?: THREE.Object3D

  /** Objekt ostepu */
  public javelinObjectFlying?: THREE.Object3D

  /** Kost na pripajanie na hraca */
  private javelinHelper?: THREE.Object3D

  /** ci bol hodeny */
  public wasThrown = false

  /** ci prave leti */
  public isFlying = false

  /** Ci je zapichnuty v zemi */
  private inGround = false

  /** Ci je aktivny shake */
  private shakeActive = false

  /** Aktualny index pre data z konfigu pre trasenie ostepu */
  private actualIndexForShake = 0

  /** Aktualny index pre morph targets influences pre trasenie ostepu */
  private activeShakeIndex = 0

  /** Aktualna max hodnota pre morph targets influences pre trasenie ostepu */
  private activeShakeValue = 0

  /** Smer pripocitavania/odpocitavania hodnot pre morph targets influences pre trasenie ostepu */
  private shakeDirection = 1

  /** objekt letovej drahy  */
  private flightPath = new FlightPath()

  /** posledna hodnota Y pri lete */
  private lastY?: number

  /** Trvanie letu vo frameoch */
  private framesFlightDuration = 0

  /** rychlost letu */
  public speed = 0

  /**
   * Konstruktor
   * @param athlete - Atlet
   */
  public constructor(private athlete: Athlete) { }

  /**
   * Vytvorenie zbrane
   */
  public create(): void {

    // musime dat rig zbrane prec z objektu hraca
    this.javelinObject = this.athlete.athleteObject.getObjectByName('javelin') as THREE.Object3D
    this.javelinObject.castShadow = true
    game.scene.add(this.javelinObject)
    this.javelinObject.frustumCulled = false

    this.javelinHelper = this.athlete.athleteObject.getObjectByName('javelin_helper')

    this.javelinObjectFlying = this.athlete.athleteObject.getObjectByName('athlete_javelin_2') as THREE.Object3D
    this.javelinObjectFlying.castShadow = true
    game.scene.add(this.javelinObjectFlying)
    this.javelinObjectFlying.frustumCulled = false
    this.javelinObjectFlying.visible = false
    this.javelinObjectFlying.matrixAutoUpdate = true
    this.flightPath.setEndPathCallback(this.endPathCallback)

  }

  /**
   * Hod
   * @param throwLength - dlzka hodu
   * @param angle - uhol
   */
  public throw(throwLength: number, angle: number): void {

    console.log('throw')
    this.isFlying = true

    if (this.javelinObject) {

      this.javelinObject.visible = false

    }
    if (this.javelinObjectFlying) {

      this.javelinHelper?.getWorldPosition(this.javelinObjectFlying.position)
      this.javelinObject?.getWorldQuaternion(this.javelinObjectFlying.quaternion)
      this.javelinObjectFlying.visible = true


      const startPos = this.javelinObjectFlying.position.clone()

      const endPoint = this.athlete.athleteObject.position.clone()
      endPoint.z -= throwLength
      const throwVector = this.athlete.athleteObject.position.clone().sub(endPoint)

      const { center, difference, min, max } = javelinConfig.gaussConfig
      const diffAngle = gauss.getValue(center, difference, min, max)
      throwVector.applyAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(diffAngle))
      const endPos = this.athlete.athleteObject.position.clone().add(throwVector)

      const centerPos = this.getPointInBetweenByPerc(startPos, endPos, 0.65)
      centerPos.y += throwLength / (2.5 + 1.5 * (45 - angle) / 20)

      this.flightPath.creatNewPath(startPos, centerPos, endPos)

    }

    const sex = playersManager.getPlayer().sex ?? PlayerSex.male
    audioManager.play(sex === PlayerSex.male ? AudioNames.yellMan : AudioNames.yellWoman)

  }

  /** Vypocet bodu medzi dvoma bodmi podla procent */
  private getPointInBetweenByPerc(pointA: THREE.Vector3, pointB: THREE.Vector3, percentage: number): THREE.Vector3 {

    let dir = pointB.clone().sub(pointA)
    const len = dir.length()
    dir = dir.normalize().multiplyScalar(len * percentage)
    return pointA.clone().add(dir)

  }

  /**
   * Co sa ma vykonat po zapichnuti ostepu
   */
  public endPathCallback = (): void => {

    console.log(`end path, ostep letel ${this.framesFlightDuration} frameov`)

    // nastavime druhe hybanie
    this.startShaking()

    // koniec
    this.onEnd()

  }

  /**
   * Prehratie zvuku komentatora
   * @param isBestAttempt - Ci ide o najlepsi pokus
   * @param isBestAttempt - Hodnota najlepsieho pokusu
   * @param actualAttempt - Hodnota aktualneho pokusu
   */
  public playCommentatorAudioOnGround(isBestAttempt: boolean, bestAttempt: number, actualAttempt: number): void {

    let audioCommentator = ''
    playersManager.setStandings(2)
    const position = playersManager.getPlayerActualPosition()

    if (corePhasesManager.disciplineActualAttempt === corePhasesManager.disciplineAttemptsCount) {

      // last attempt
      audioCommentator = AudioNames.commentatorAfterLastThrow6

      if (position === 1) {

        audioCommentator = isBestAttempt ?
          AudioNames.commentatorAfterLastThrow1Best :
          AudioNames.commentatorAfterLastThrow1NoImprovement

      } else if (position <= 3) {

        audioCommentator = isBestAttempt ?
          AudioNames.commentatorAfterLastThrow23Best :
          AudioNames.commentatorAfterLastThrow23NoImprovement

      } else if (position <= (modes.isDailyLeague() || modes.isTournament() || modes.isBossCompetition() ? 10 : 5)) {

        audioCommentator = AudioNames.commentatorAfterLastThrow45

      }

      if (modes.isEventBossFight() && position === 2) audioCommentator = AudioNames.commentatorAfterLastThrow6

    } else {

      // not last attempt
      if (isBestAttempt) {

        audioCommentator = AudioNames.commentatorAfterThrow6

        if (position === 1) {

          audioCommentator = AudioNames.commentatorAfterThrow1

        } else if (position <= 3) {

          audioCommentator = AudioNames.commentatorAfterThrow23

        } else if (position <= 5) {

          audioCommentator = AudioNames.commentatorAfterThrow45

        }

        if (actualAttempt >= this.athlete.personalBest) audioCommentator = AudioNames.commentatorAfterThrowPB
        if (actualAttempt >= endManager.worldRecord) audioCommentator = AudioNames.commentatorAfterThrowWR

      } else {

        audioCommentator = (position <= 3 && actualAttempt >= bestAttempt * 0.95) ?
          AudioNames.commentatorAfterThrowNoImprovementGood :
          AudioNames.commentatorAfterThrowNoImprovementBad

      }

    }

    audioManager.play(audioCommentator)

  }

  /**
   * Prehratie zvukov pri dopade ostepu na zem
   */
  private playAudioOnGround(): void {

    audioManager.fadeOutAudio(AudioNames.audienceCheering, 1500)
    audioManager.play(AudioNames.audienceIntroNoise)

    const bestAttempt = playersManager.getBestResult(PlayersSortTypes.descending, undefined, true)
    const attemptIndex = corePhasesManager.disciplineActualAttempt - 1
    const actualAttempt = playersManager.getPlayer()?.resultsArr?.[attemptIndex].main ?? -1
    const isBestAttempt = actualAttempt >= bestAttempt
    const audio = isBestAttempt ? AudioNames.audienceJavelinOnGroundBest : AudioNames.audienceJavelinOnGround
    audioManager.play(audio)

    if (isBestAttempt) {

      gsap.to({}, {
        duration: 3,
        onComplete: () => {

          audioManager.fadeOutAudio(AudioNames.audienceJavelinOnGroundBest, 1000)

        }
      })

    }

    // este komentator
    audioHelper.setTimerToPlayCommentatorJavelinOnGround(isBestAttempt, bestAttempt, actualAttempt)

  }

  /**
   * Zobrazime hlasku o pocte metrov
   * @param actualAttempt - Vysledok aktualny
   */
  private showMessageMeters(actualAttempt: number): void {

    let worldRecord = endManager.worldRecord
    const personalBest = player.personalBest
    if (personalBest > worldRecord) worldRecord = personalBest

    let text = ''
    if (!(modes.isTournament() || modes.isTrainingMode() || modes.isTutorial())) {

      if (actualAttempt > personalBest) text = 'newPersonalBest'
      if (actualAttempt === personalBest) text = 'personalBest'
      if (actualAttempt > worldRecord) text = 'newWorldRecord'
      if (actualAttempt === worldRecord) text = 'worldRecord'

    }


    textMessageMetersState().$patch({
      showMessage: true,
      firstLineText: text,
      secondLineText: actualAttempt.toFixed(2)
    })

  }

  /**
   * Co robime na konci - chceme ist dalej na emociu a pod
   */
  public onEnd(): void {

    let result = this.athlete.throwFinalLength
    if (this.athlete.wasFoul) {

      result = 0
      playersManager.setPlayerResults(0, 1, 0)
      audioManager.play(AudioNames.commentatorAfterThrowFoul)

    } else {

      playersManager.setPlayerResults(result, 0, 0)
      this.playAudioOnGround()
      this.showMessageMeters(result)
      this.athlete.checkNewPersonalBest(result)

    }

    stateManager.showAttemptsTable()

    this.isFlying = false
    this.inGround = true

    disciplinePhasesManager.phaseFlying.finishPhase()
    if (modes.isTutorial()) return

    miniTableState().$patch({
      rowsData: StateManager.getDataForMiniTable(result),
      show: corePhasesManager.disciplineActualAttempt % corePhasesManager.provisionalResultsFrequency !== 0 &&
        !modes.isTutorial() && !modes.isTrainingMode()
    })
    gamePhaseState().showRecords = false

  }

  /**
   * Zaciatok trasenia ostepu
   */
  public startShaking(): void {

    if (!this.javelinObjectFlying) return

    // zresetujeme morph target influences
    const mesh = this.javelinObjectFlying as THREE.Mesh
    mesh.morphTargetInfluences = [0, 0, 0, 0]

    this.actualIndexForShake = 0
    this.setNextDataForShake()

    this.shakeDirection = 1
    this.shakeActive = true

  }

  /**
   * Nastavenie novych dat pre shake
   */
  private setNextDataForShake(): void {

    const { indexes } = this.isFlying ? javelinConfig.shake.flying : javelinConfig.shake.ground

    this.activeShakeIndex = indexes[this.actualIndexForShake]
    this.activeShakeValue = 0

    this.actualIndexForShake += 1

    if (this.actualIndexForShake === indexes.length) {

      this.actualIndexForShake = 0
      if (this.inGround) this.shakeActive = false

    }

  }

  /**
   * Spravovanie trasenia ostepu
   */
  private manageShaking(): void {

    if (!this.shakeActive || !this.javelinObjectFlying || (!this.isFlying && !this.inGround)) return

    const { addValuePerFrame, values } = this.isFlying ? javelinConfig.shake.flying : javelinConfig.shake.ground

    this.activeShakeValue += (addValuePerFrame * this.shakeDirection)

    // kontrolujeme hornu hranicu
    if (this.activeShakeValue >= values[this.actualIndexForShake]) {

      this.activeShakeValue = values[this.actualIndexForShake]
      this.shakeDirection = -1

    }

    // kontrolujeme spodnu hranicu
    if (this.activeShakeValue <= 0) {

      this.activeShakeValue = 0
      this.shakeDirection = 1
      this.setNextDataForShake()


    }

    const mesh = this.javelinObjectFlying as THREE.Mesh
    if (mesh.morphTargetInfluences) mesh.morphTargetInfluences[this.activeShakeIndex] = this.activeShakeValue

  }

  /**
   * Aktualizovanie kazdy frame
   */
  public update(): void {

    this.manageShaking()

    if (!this.isFlying) return
    if (this.javelinObjectFlying?.position) {

      const point = this.flightPath.getNextPoint(this.speed)
      this.framesFlightDuration += 1
      mainState().javelinSpeed = this.speed

      if (point) {

        const coef = (this.lastY === undefined || this.lastY < point.y) ?
          javelinConfig.speedDecelerationUp :
          javelinConfig.speedAccelerationDown
        this.speed *= coef

        this.javelinObjectFlying.position.set(point.x, point.y, point.z)
        const lookAt = this.flightPath.getLookAtPoint()
        if (lookAt) {

          this.javelinObjectFlying.lookAt(lookAt)

        }

        this.lastY = point.y

      }

    }

  }

  /**
   * reset
   */
  public reset(): void {

    this.wasThrown = false
    this.isFlying = false
    this.inGround = false
    this.shakeActive = false
    this.lastY = undefined
    this.framesFlightDuration = 0
    if (this.javelinObject) {

      this.javelinObject.visible = true

    }
    if (this.javelinObjectFlying) {

      this.javelinObjectFlying.visible = false

    }
    this.flightPath.reset()

  }

}